'use client'

import { useSearchPickerContext } from '@trustoo/components/searchPicker/SearchPickerContext'
import LinearLoader from '@trustoo/components/ui/LinearLoader'
import clsx from 'clsx'
import { ComponentPropsWithoutRef, ReactNode, useCallback, useEffect } from 'react'

interface SearchPickerContentProps extends ComponentPropsWithoutRef<'div'> {
    children: ReactNode
    showLoader?: boolean
    referenceId?: string
}

/**
 * SearchPicker.Content - UI Container & Event Detection Layer
 *
 * **Role in Compound Structure:**
 * This component serves as the visual container that wraps the input and dropdown.
 * It handles UI concerns like styling, layout, and DOM event detection (blur events).
 * It follows the **Single Responsibility Principle** by separating UI concerns from
 * business logic.
 *
 * **Responsibilities:**
 * - Provides the visual container (background, border, shadow)
 * - Wraps both the input field and dropdown in a single container
 * - Calculates and manages dropdown max-height based on available viewport space
 * - Uses referenceId to calculate available space relative to a container element
 * - Displays loading indicator when fetching options
 */
const SearchPickerContent = ({
    children,
    className,
    showLoader = true,
    referenceId,
    ...rest
}: SearchPickerContentProps) => {
    const { isOpen, isLoading, setMaxHeight, contentRef, settings } = useSearchPickerContext()

    // Calculate max height based on available space
    const calculateMaxHeight = useCallback(() => {
        if (!setMaxHeight || !contentRef.current || !isOpen) {
            return
        }

        const inputElementBottom =
            contentRef.current.getBoundingClientRect().top + settings.inputFieldHeight
        const containerElement = referenceId ? document.getElementById(referenceId) : undefined
        const availableSpace =
            (containerElement
                ? containerElement.getBoundingClientRect().bottom
                : window.innerHeight) - inputElementBottom
        const calculatedMaxHeight = availableSpace - settings.marginBottom

        setMaxHeight(
            calculatedMaxHeight > settings.maxHeight
                ? settings.maxHeight
                : calculatedMaxHeight < settings.minHeight
                  ? settings.minHeight
                  : calculatedMaxHeight,
        )
    }, [
        settings.minHeight,
        settings.maxHeight,
        settings.marginBottom,
        settings.inputFieldHeight,
        isOpen,
        setMaxHeight,
        referenceId,
    ])

    // Calculate max height when dropdown opens
    useEffect(() => {
        if (isOpen) {
            calculateMaxHeight()
        }
    }, [isOpen, calculateMaxHeight])

    return (
        <div
            ref={contentRef}
            className={clsx(
                isOpen &&
                    '!absolute -top-1 -left-1 -right-1 p-1 z-[999] bg-white rounded-md5 shadow-[0_1px_3px_rgba(60,64,67,0.3),_0_4px_8px_3px_rgba(60,64,67,0.15)]',
                className,
            )}
            {...rest}>
            {showLoader && isLoading && <LinearLoader absolute className="z-[1]" />}
            {children}
        </div>
    )
}

export default SearchPickerContent
